import { useState } from "react";
import { Link } from "wouter";
import { Check, Loader2, Siren } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatTimeAgo } from "@/lib/ai-engine";
import { reachedAnyone, sinceText, useAckEscalation, useOpenEscalations, type Escalation } from "@/hooks/use-escalations";
import { useNow } from "@/components/content-simple/agentic";
import { DeliveryLine } from "@/components/escalation-ui";

/** Rows shown before "Show N more": two on a wide screen, one on a phone (the banner is pinned, so it must not eat the screen). */
const SHOWN_WIDE = 2;
const SHOWN_NARROW = 1;

/**
 * One open P0: "P0: <title> — since 3:42 PM (2h ago); delivery: Telegram ✓ / email ✗", with an Acknowledge button.
 * On a phone the words take the full width and the disclosure and the button share the row beneath them; on a wide screen the button sits to the right.
 */
export function P0Row({ e }: { e: Escalation }) {
  const ack = useAckEscalation();
  const now = useNow(60_000);
  const [open, setOpen] = useState(false);
  const since = sinceText(e.createdAt, now);
  // "A failed notification is itself a P0": when no channel got this through, say so plainly rather than leave a row of crosses to be decoded.
  const unreached = !reachedAnyone(e);
  const detailLines = e.channels.filter((c) => c.detail);
  const hasDetails = !!e.body || detailLines.length > 0;
  return (
    <div
      className="grid grid-cols-[1fr_auto] items-start gap-x-3 gap-y-1.5 [grid-template-areas:'head_head'_'warn_warn'_'tog_ack'_'det_det'] sm:gap-x-4 sm:[grid-template-areas:'head_ack'_'warn_ack'_'tog_ack'_'det_det']"
      data-testid={`escalation-${e.id}`}
      data-level={e.level}
    >
      <p className="min-w-0 break-words text-sm leading-snug text-red-100/95 [grid-area:head]" data-testid="escalation-headline">
        <span className="font-bold text-white">P0: {e.title}</span>
        {since && (
          <span>
            {" "}— since {since}
            {e.createdAt ? ` (${formatTimeAgo(e.createdAt, now)})` : ""}
          </span>
        )}
        <span>; </span>
        <DeliveryLine escalation={e} />
      </p>
      {unreached && (
        <p className="text-xs font-medium text-red-50 [grid-area:warn]" data-testid="escalation-unreached">
          Nobody has been told about this yet; delivery is retried automatically. Check{" "}
          <Link href="/settings?tab=notifications" className="underline underline-offset-2">
            Settings → Notifications
          </Link>
          .
        </p>
      )}
      <div className="self-center [grid-area:tog]">
        {hasDetails && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="text-xs text-red-100/90 underline underline-offset-2 hover:text-white"
            data-testid={`button-escalation-details-${e.id}`}
          >
            {open ? "Hide details" : "What happened"}
          </button>
        )}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={ack.isPending}
        onClick={() => ack.mutate(e.id)}
        className="h-8 shrink-0 self-start border-red-200/50 bg-transparent px-3 text-red-50 hover:bg-red-500/30 hover:text-white [grid-area:ack]"
        data-testid={`button-ack-${e.id}`}
      >
        {ack.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        Acknowledge
      </Button>
      {open && hasDetails && (
        <div className="space-y-1 break-words text-xs text-red-100/85 [grid-area:det]" data-testid={`escalation-details-${e.id}`}>
          {e.body && <p className="whitespace-pre-wrap">{e.body}</p>}
          {detailLines.map((c, i) => (
            <p key={`${c.channel}-${i}`} className="text-red-100/70">
              {c.channel}: {c.detail}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Pinned to the top of Content Studio and the Draper page while any P0 is open. Nothing here can be dismissed except by
 * acknowledging, because a P0 that scrolls away or times out is the failure it exists to prevent.
 */
export function EscalationBanner({ sticky = false, className = "" }: { sticky?: boolean; className?: string }) {
  const { p0 } = useOpenEscalations();
  const narrow = useIsMobile();
  const [all, setAll] = useState(false);
  if (p0.length === 0) return null;
  const limit = narrow ? SHOWN_NARROW : SHOWN_WIDE;
  const shown = all ? p0 : p0.slice(0, limit);
  return (
    <div className={`${sticky ? "sticky top-0 z-30 bg-background" : ""} ${className}`} data-testid="banner-escalation-wrap">
      <div
        role="alert"
        aria-live="assertive"
        className="flex max-h-[45vh] items-start gap-3 overflow-y-auto rounded-xl border border-red-500/60 bg-red-950 px-3 py-3 text-red-50 shadow-lg shadow-red-950/40 sm:px-4"
        data-testid="banner-escalation"
        data-count={p0.length}
      >
        <Siren size={18} className="mt-0.5 shrink-0 text-red-300" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-3">
          {shown.map((e) => (
            <P0Row key={String(e.id)} e={e} />
          ))}
          {p0.length > limit && (
            <button type="button" onClick={() => setAll((v) => !v)} className="text-xs font-medium text-red-100 underline underline-offset-2" data-testid="button-escalation-more">
              {all ? "Show fewer" : `Show ${p0.length - limit} more urgent ${p0.length - limit === 1 ? "alert" : "alerts"}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
